import React, { useState } from 'react';
import type {
  PortalCancelResult,
  PortalDownsellFailureReason,
  PortalSubscriptionView,
} from '@/api/portal';
import { findStripePrice, formatStripePrice } from '@/utils/formatStripePrice';
import {
  formatPortalDate,
  isDownsellEligible,
  usePortalActions,
  usePortalBlockPresence,
  type DownsellOfferConfig,
} from './portalActionsContext';
import { PortalInvite } from './PortalEntryRequestForm';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { resolvePortalText } from '@/utils/portalLegacyDefaults';
import { useInertPreviewProps } from './inertPreview';
import { PORTAL_GHOST_BUTTON_STYLE } from './portalGhostButtonStyle';
import { offerCtaTextColor } from './offerCtaTextColor';

/**
 * WEB-1152 (П9) атом 3 — блок `cancel_button` портала отмены.
 * WEB-1183: отмена больше не срабатывает с одного клика — клик открывает
 * шаг подтверждения («точно отменить?»), а если на странице настроен блок
 * downsell_offer, тот же шаг предлагает скидку вместо ухода: принятие
 * оффера переиспользует контурный `portal.downsell` (тот же путь, что и
 * обычный downsell_offer блок — см. DownsellOfferBlock.tsx) и НЕ отменяет
 * подписку; отказ ведёт к отдельной кнопке подтверждения отмены.
 *
 * КОМПЛАЕНС: кнопка отмены доступна всегда и не прячется за оффером
 * удержания — блок рендерит её для каждой ещё не отменённой подписки
 * независимо от того, настроен ли рядом downsell_offer. Отмена плановая
 * (cancel_at_period_end): после успеха показываем «отмена запланирована
 * на <дата>», доступ живёт до конца оплаченного периода.
 */
export interface CancelSubscriptionButtonProps {
  label?: string;
  /**
   * WEB-1692 юнит 3 (репорт владельца 24.08 «не хватает настроек размера
   * текста и настроек заливки самой кнопки внутри»). Сюда приходила ТОЛЬКО
   * надпись; заливку и цвет текста собирает тот же `buttonSurfaceStyle`
   * (WEB-1651-2), что у кнопок квиза и пейволла.
   *
   * ⛔ РЕШЕНИЕ ВЛАДЕЛЬЦА 25.08, дословно: «кнопка отмены подписки должна
   * настраиваться и иметь опции точно такие же, как все другие кнопки… Если
   * наш пользователь сделает её невидимой — это уже его проблемы». Никаких
   * нижних пределов размера, проверок контраста и предупреждений — прежняя
   * рекомендация «ограничить ради комплаенса» ОТМЕНЕНА, не переоткрывать.
   *
   * ⚠ КРАСНОЕ СОСТОЯНИЕ ПОДТВЕРЖДЕНИЯ остаётся красным ПРИ ПУСТЫХ ПОЛЯХ: его
   * собственные строки (`#ff7875` рамка, `#cf1322` текст) стоят в исходнике
   * дословно, а накладка автора ложится ПОСЛЕ них — то есть по решению
   * владельца действует и там, но только когда автор её задал.
   */
  surfaceStyle?: React.CSSProperties;
  /** Кегль надписи (общее поле кнопочной семьи). Пусто = прежние 14px. */
  fontSize?: number;
  /**
   * WEB-1905: язык ВОРОНКИ. Надпись кнопки, шаг подтверждения, карточка
   * оффера и все отказы приходят из словаря плеера по нему — раньше это были
   * английские константы, одинаковые на воронке любого языка.
   */
  language?: string | null;
}

// WEB-1183: «Cancel anyway» рисуется тревожным цветом — на мобильной ширине
// обе кнопки шага подтверждения идут блоком на всю ширину друг под другом
// (PORTAL_GHOST_BUTTON_STYLE/dangerButtonStyle оба width: 100%), «Keep subscription» выше
// «Cancel anyway» — случайный тап по верхней части экрана попадает на
// безопасный по умолчанию вариант, не на отмену.
const dangerButtonStyle: React.CSSProperties = {
  ...PORTAL_GHOST_BUTTON_STYLE,
  marginTop: 8,
  border: '1px solid #ff7875',
  color: '#cf1322',
};

const offerCardStyle: React.CSSProperties = {
  border: '1px solid #e5e5e5',
  borderRadius: 12,
  padding: 16,
  marginBottom: 8,
  textAlign: 'left',
};

const offerCtaStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 12,
  padding: '10px 16px',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
};

const noteStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#8c8c8c',
  marginTop: 4,
};

const failureNoteStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 13,
  color: '#cf1322',
};

const confirmTitleStyle: React.CSSProperties = {
  fontSize: 14,
  marginBottom: 8,
};

/**
 * WEB-1183: карточка предложения скидки НА ШАГЕ подтверждения отмены.
 * Переиспользует тот же контурный `portal.downsell`, что и обычный
 * downsell_offer блок (DownsellOfferBlock.tsx) — сетевой путь один, здесь
 * только собственная разметка и локальное состояние принятия/отказа.
 */
function CancelDownsellCard({
  offer,
  subscriptionId,
  onAccepted,
  language,
}: {
  offer: DownsellOfferConfig;
  subscriptionId: string;
  onAccepted: () => void;
  language?: string | null;
}) {
  const t = getPublicQuizStrings(language).portal;
  const portal = usePortalActions();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<PortalDownsellFailureReason | null>(
    null,
  );

  // WEB-1905 (остаток приёмки 07.09): старое английское умолчание, лежащее в
  // содержимом пейволлов до 05.09, считается НЕзаданным — см.
  // `utils/portalLegacyDefaults.ts`. Авторский текст остаётся как есть.
  const resolvedTitle = resolvePortalText(offer.title, t.offerTitle);
  const resolvedCta = resolvePortalText(offer.ctaLabel, t.offerCta);
  const targetPrice = findStripePrice(offer.stripePrices, offer.targetPriceId);
  const targetPriceLabel = targetPrice ? formatStripePrice(targetPrice) : null;
  // Терминальные отказы (409): повтор не сработает никогда — кнопку убираем.
  const terminal =
    failure === 'subscription_cancelling' ||
    failure === 'offer_not_configured' ||
    // WEB-2457 п.4: скидка закрыта тарифом клиента — повтор не поможет.
    failure === 'downsell_unavailable';

  const onAccept = async () => {
    if (busy || !portal) return;
    setBusy(true);
    setFailure(null);
    const outcome = await portal.downsell(subscriptionId, offer.targetPriceId);
    setBusy(false);
    if (outcome.ok) {
      onAccepted();
    } else {
      setFailure(outcome.reason);
    }
  };

  // WEB-1692 юнит 5 — ВТОРАЯ ДВЕРЬ ОДНОЙ НАСТРОЙКИ. Разметка тут своя, но
  // оформление автор задаёт ОДИН раз на блоке `downsell_offer`, и накладки
  // приезжают сюда вместе с его конфигурацией (`DownsellOfferConfig.appearance`).
  // Иначе автор красил бы оффер, видел его на канвасе, а покупатель на шаге
  // отмены получал бы прежний вид — настройка, доехавшая до одной двери из двух.
  const look = offer.appearance;

  return (
    <div
      data-testid={`portal-cancel-downsell-${subscriptionId}`}
      style={{
        ...offerCardStyle,
        ...(look?.cardBackgroundColor
          ? { background: look.cardBackgroundColor }
          : {}),
      }}
    >
      <p style={{ fontWeight: 700, ...(look?.title ?? {}) }}>{resolvedTitle}</p>
      {offer.text && (
        <p style={{ marginTop: 4, ...(look?.secondary ?? {}) }}>{offer.text}</p>
      )}
      {targetPriceLabel && (
        <p
          style={{ marginTop: 4, color: '#8c8c8c', ...(look?.secondary ?? {}) }}
        >
          {t.newPrice(targetPriceLabel)}
        </p>
      )}
      {!terminal && (
        <button
          type="button"
          style={{
            ...offerCtaStyle,
            ...(() => {
              const background =
                look?.ctaBackgroundColor ?? offer.primaryColor ?? '#1677ff';
              return { background, color: offerCtaTextColor(background) };
            })(),
            opacity: busy ? 0.6 : 1,
            ...(look?.cta ?? {}),
          }}
          disabled={busy}
          onClick={() => void onAccept()}
        >
          {resolvedCta}
        </button>
      )}
      {failure === 'subscription_cancelling' && (
        <p
          data-testid={`portal-cancel-downsell-not-eligible-${subscriptionId}`}
          style={failureNoteStyle}
        >
          {t.downsellNotEligible}
        </p>
      )}
      {(failure === 'offer_not_configured' ||
        failure === 'downsell_unavailable') && (
        <p
          data-testid={`portal-cancel-downsell-unavailable-${subscriptionId}`}
          style={failureNoteStyle}
        >
          {t.downsellUnavailable}
        </p>
      )}
      {failure === 'unavailable' && (
        <p style={failureNoteStyle}>{t.downsellError}</p>
      )}
    </div>
  );
}

function CancelRow({
  sub,
  label,
  cancel,
  downsellOffer,
  ownStyle,
  language,
}: {
  sub: PortalSubscriptionView;
  label: string;
  cancel: (subscriptionId: string) => Promise<PortalCancelResult | null>;
  downsellOffer: DownsellOfferConfig | null;
  ownStyle: React.CSSProperties;
  language?: string | null;
}) {
  const t = getPublicQuizStrings(language).portal;
  const [stage, setStage] = useState<'idle' | 'confirm'>('idle');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [downsellAccepted, setDownsellAccepted] = useState(false);

  const offerEligible = downsellOffer
    ? isDownsellEligible(sub, downsellOffer)
    : false;

  const onConfirmCancel = async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    const result = await cancel(sub.subscriptionId);
    setBusy(false);
    // Успех отражается провайдером (cancelAtPeriodEnd=true) — строка
    // перерисуется в «отмена запланирована …» уровнем выше, шаг
    // подтверждения можно закрыть.
    if (result === null) {
      setFailed(true);
    } else {
      setStage('idle');
    }
  };

  if (stage === 'idle') {
    return (
      <div style={{ marginBottom: 8 }}>
        <button
          type="button"
          // WEB-1692: вид от автора — ПОСЛЕ прежних строк; пусто = {}.
          style={{ ...PORTAL_GHOST_BUTTON_STYLE, ...ownStyle }}
          onClick={() => setStage('confirm')}
          data-testid={`portal-cancel-${sub.subscriptionId}`}
        >
          {label}
        </button>
        {/* Ревизия десятки 7 (блокер): после принятия скидки шаг подтверждения
            ЗАКРЫВАЕТСЯ (setStage('idle') в onAccepted ниже) — раньше человек
            оставался в confirm-ветке без единой кнопки и без выхода, а кнопка
            отмены исчезала до перезагрузки страницы (нарушение комплаенс-
            инварианта из шапки файла). Подтверждение успеха скидки печатается
            здесь, рядом с живой кнопкой отмены. */}
        {downsellAccepted && (
          <p
            data-testid={`portal-cancel-downsell-success-${sub.subscriptionId}`}
            // Ревизия пачки 7 (F1): текст после принятия скидки — часть роли
            // Secondary оффера (так обещают докблок реестра и подпись панели);
            // без накладки вторая дверь отличалась бы от канваса на этом
            // состоянии. Пусто = прежний noteStyle.
            style={{
              ...noteStyle,
              ...(downsellOffer?.appearance?.secondary ?? {}),
            }}
          >
            {t.downsellSuccess}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      style={{ marginBottom: 8 }}
      data-testid={`portal-cancel-confirm-${sub.subscriptionId}`}
    >
      <p style={confirmTitleStyle}>{t.cancelConfirmTitle}</p>
      {downsellOffer && offerEligible && !downsellAccepted && (
        <CancelDownsellCard
          offer={downsellOffer}
          subscriptionId={sub.subscriptionId}
          onAccepted={() => {
            setDownsellAccepted(true);
            setStage('idle');
          }}
          language={language}
        />
      )}
      <button
        type="button"
        style={{ ...PORTAL_GHOST_BUTTON_STYLE, ...ownStyle }}
        disabled={busy}
        onClick={() => setStage('idle')}
        data-testid={`portal-cancel-keep-${sub.subscriptionId}`}
      >
        {t.cancelConfirmKeep}
      </button>
      <button
        type="button"
        // WEB-1692 (решение владельца 25.08): настройки действуют и на
        // красном состоянии подтверждения — но только когда автор их задал.
        // Пусто = прежний красный дословно.
        style={{ ...dangerButtonStyle, ...ownStyle, opacity: busy ? 0.6 : 1 }}
        disabled={busy}
        onClick={() => void onConfirmCancel()}
        data-testid={`portal-cancel-confirm-proceed-${sub.subscriptionId}`}
      >
        {t.cancelConfirmProceed}
      </button>
      {failed && <p style={noteStyle}>{t.cancelError}</p>}
    </div>
  );
}

export function CancelSubscriptionButton({
  label,
  surfaceStyle,
  fontSize,
  language,
}: CancelSubscriptionButtonProps) {
  const t = getPublicQuizStrings(language).portal;
  const portal = usePortalActions();
  // Регресс-фикс: блок объявляет себя видимым — хозяина приглашения провайдер
  // выбирает среди примонтированных блоков ТЕКУЩЕГО экрана. Ответ синхронный:
  // приглашение рисуется уже на первом кадре (ревизия 5, дефект 2).
  const isInviteHost = usePortalBlockPresence();
  // WEB-1905 (остаток приёмки 07.09): «Cancel subscription» из содержимого
  // старого пейволла перебивало словарь — покупатель русской воронки читал
  // английскую кнопку. Старое умолчание из реестра = пусто.
  const resolvedLabel = resolvePortalText(label, t.cancelLabel);
  // WEB-1723: на канвасе редактора инертность держится без `disabled` —
  // иначе выключенная кнопка гасит клик и блок нельзя выбрать (см.
  // inertPreview.ts). Публичный портал получает `disabled` как раньше.
  const inertProps = useInertPreviewProps();
  // WEB-1692: заливка/цвет надписи от сборщика вида + свой кегль. Оба пустые —
  // объект пуст, и style кнопки байт-в-байт прежний.
  const ownStyle: React.CSSProperties = {
    ...(surfaceStyle ?? {}),
    ...(fontSize ? { fontSize } : {}),
  };

  if (!portal) {
    // Канвас редактора — кнопка видна (комплаенс-макет), но инертна.
    return (
      <div style={{ marginBottom: 16 }}>
        <button
          type="button"
          style={{ ...PORTAL_GHOST_BUTTON_STYLE, ...ownStyle }}
          {...inertProps}
        >
          {resolvedLabel}
        </button>
      </div>
    );
  }

  // Ревизия WEB-1152 (п.4): loading — РАНЬШЕ проверки входа (как в
  // SubscriptionListBlock), иначе кнопка мигает «ссылка недействительна»,
  // пока ?code= ещё резолвится. WEB-1192 (п.3): hasAccess вместо guid из URL.
  if (portal.loading) return null;
  if (!portal.hasAccess) {
    // Ревизия 2 (дефект 5): кнопка отмены печатала ТОТ ЖЕ текст и ТОТ ЖЕ
    // testid, что список, но форму запроса ссылки рендерил только список —
    // на странице с обоими блоками абзац дублировался, на странице без
    // списка обещал форму, которой рядом нет. Теперь приглашение (текст +
    // форма) печатает ровно один назначенный провайдером блок.
    return <PortalInvite active={isInviteHost} language={language} />;
  }
  if (portal.error) {
    return <p style={{ marginBottom: 16 }}>{t.loadError}</p>;
  }
  if (portal.subscriptions.length === 0) {
    return <p style={{ marginBottom: 16 }}>{t.empty}</p>;
  }

  return (
    <div style={{ marginBottom: 16 }}>
      {portal.subscriptions.map((sub) => {
        if (sub.status === 'canceled') {
          return (
            <p key={sub.subscriptionId} style={noteStyle}>
              {t.subscriptionCanceled}
            </p>
          );
        }
        if (sub.cancelAtPeriodEnd) {
          const date = formatPortalDate(sub.currentPeriodEnd, language);
          return (
            <p
              key={sub.subscriptionId}
              style={noteStyle}
              data-testid={`portal-cancel-scheduled-${sub.subscriptionId}`}
            >
              {date ? t.cancellationScheduledOn(date) : t.cancellationScheduled}
            </p>
          );
        }
        return (
          <CancelRow
            key={sub.subscriptionId}
            sub={sub}
            label={resolvedLabel}
            cancel={portal.cancel}
            downsellOffer={portal.downsellOffer}
            ownStyle={ownStyle}
            language={language}
          />
        );
      })}
    </div>
  );
}
